import React, { useState } from 'react';
import { BookModel, SiteSettingModel, SUBJECT_TYPES, GRADE_TYPES, PUBLISHERS } from '../../../../models';
import { Search, Info, Plus, Book as BookIcon, Filter } from 'lucide-react';
import BookCard from './BookCard';

interface BookListProps {
    books: BookModel[];
    siteSettings: SiteSettingModel;
    onDeleteBook: (book: BookModel) => void;
    onEditBook: (book: BookModel) => void;
    selectedBookId?: string;
    currentUserId?: string;
    currentUserRole?: string;
}

const BookList: React.FC<BookListProps> = ({ books, siteSettings, onDeleteBook, onEditBook, selectedBookId, currentUserId, currentUserRole }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSubject, setFilterSubject] = useState('all');
    const [filterPublisher, setFilterPublisher] = useState('all');
    const [filterGrade, setFilterGrade] = useState('all');
    const [filterYear, setFilterYear] = useState('all');

    // Extract unique years from books
    const years = Array.from(new Set(books.map(b => b.year).filter(Boolean))).sort().reverse();

    const filteredBooks = books.filter(b => {
        const matchesSearch = b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.publisher.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.subject.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesSubject = filterSubject === 'all' || b.subject === filterSubject;
        const matchesPublisher = filterPublisher === 'all' || b.publisher === filterPublisher;
        const matchesGrade = filterGrade === 'all' || b.grade === filterGrade;
        const matchesYear = filterYear === 'all' || b.year === filterYear;

        return matchesSearch && matchesSubject && matchesPublisher && matchesGrade && matchesYear;
    });

    const subjects = siteSettings.subjects && siteSettings.subjects.length > 0 ? siteSettings.subjects : SUBJECT_TYPES;
    const grades = siteSettings.bookGrades && siteSettings.bookGrades.length > 0 ? siteSettings.bookGrades : GRADE_TYPES;
    const publishers = siteSettings.publishers && siteSettings.publishers.length > 0 ? siteSettings.publishers : PUBLISHERS;

    return (
        <div className="h-full flex flex-col p-4 space-y-4">
            {/* Search Area */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={14} />
                        <input
                            type="text"
                            placeholder="교재명/과목/출판사 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs font-semibold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                    </div>
                    <button
                        onClick={() => onEditBook(null as any)}
                        className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all shadow-md shadow-indigo-100 hover:scale-105 active:scale-95 flex items-center justify-center shrink-0"
                        title="새 교재 생성"
                    >
                        <Plus size={16} strokeWidth={3} />
                    </button>
                </div>

                {/* Filters Row */}
                {/* Filters Row */}
                <div className="grid grid-cols-2 gap-2">
                    <select
                        value={filterSubject}
                        onChange={(e) => setFilterSubject(e.target.value)}
                        className="bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-600 outline-none focus:ring-1 focus:ring-indigo-500 appearance-none"
                    >
                        <option value="all">전체 과목</option>
                        {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select
                        value={filterPublisher}
                        onChange={(e) => setFilterPublisher(e.target.value)}
                        className="bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-600 outline-none focus:ring-1 focus:ring-indigo-500 appearance-none"
                    >
                        <option value="all">전체 출판사</option>
                        {publishers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select
                        value={filterGrade}
                        onChange={(e) => setFilterGrade(e.target.value)}
                        className="bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-600 outline-none focus:ring-1 focus:ring-indigo-500 appearance-none"
                    >
                        <option value="all">전체 대상</option>
                        {grades.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <select
                        value={filterYear}
                        onChange={(e) => setFilterYear(e.target.value)}
                        className="bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-600 outline-none focus:ring-1 focus:ring-indigo-500 appearance-none"
                    >
                        <option value="all">전체 연도</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            <div className="flex items-center justify-between px-1 border-t border-slate-50 pt-4">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <BookIcon size={12} /> 검색 결과 ({filteredBooks.length} / {books.length})
                </span>
            </div>

            {/* List Area */}
            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar space-y-4">
                {filteredBooks.map(book => (
                    <BookCard
                        key={book.id}
                        book={book}
                        onDelete={onDeleteBook}
                        onEdit={onEditBook}
                        isSelected={selectedBookId === book.id}
                        currentUserId={currentUserId}
                        currentUserRole={currentUserRole}
                    />
                ))}
            </div>

            {filteredBooks.length === 0 && (
                <div className="py-20 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                    <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-slate-200 mx-auto mb-4 border border-slate-50">
                        <BookIcon size={32} />
                    </div>
                    <p className="text-[11px] font-bold text-slate-400">조건에 맞는 교재가 없습니다.</p>
                </div>
            )}
        </div>
    );
};

export default BookList;
